export type ApplicationColumnId =
  | 'app'
  | 'appType'
  | 'appSource'
  | 'platform'
  | 'vendor'
  | 'dateSubmitted'
  | 'devices'
  | 'countriesContacted'
  | 'maliciousScore'
  | 'policyViolations'
  | 'actions';

/** Matches quokka-ui's QColumnDefinition, which the published package does not ship types for. */
export interface ApplicationColumnDefinition {
  id: ApplicationColumnId;
  label: string;
  locked?: boolean;
  defaultVisible?: boolean;
  isNew?: boolean;
}

/**
 * Columns available in the Applications list, in default order. App Source,
 * Platform, Vendor and Malicious Score are opt-in.
 */
export const APPLICATION_COLUMNS: ApplicationColumnDefinition[] = [
  { id: 'app', label: 'App Name', locked: true },
  { id: 'appType', label: 'App Type' },
  { id: 'appSource', label: 'App Source', defaultVisible: false },
  { id: 'platform', label: 'Platform', defaultVisible: false },
  { id: 'vendor', label: 'Vendor', defaultVisible: false },
  { id: 'dateSubmitted', label: 'Date Submitted' },
  { id: 'maliciousScore', label: 'Malicious Score', defaultVisible: false },
  { id: 'devices', label: 'Devices' },
  { id: 'countriesContacted', label: 'Countries Contacted' },
  { id: 'policyViolations', label: 'Policy Violations' },
  { id: 'actions', label: 'Actions', locked: true },
];

/** Columns whose values only exist once analysis has finished. */
export const ANALYSIS_RESULT_COLUMN_IDS: ReadonlySet<ApplicationColumnId> = new Set([
  'countriesContacted',
  'maliciousScore',
  'policyViolations',
]);

/**
 * Groups visible columns into table cells. For rows whose analysis is not
 * finished, each run of adjacent analysis-result columns collapses into one
 * spanning cell, and only the first run carries the status message.
 */
export const getApplicationRowCells = (
  columnIds: ApplicationColumnId[],
  analysisFinished: boolean,
): { id: ApplicationColumnId; colSpan: number; isStatus: boolean; showStatus: boolean }[] => {
  if (analysisFinished) return columnIds.map(id => ({ id, colSpan: 1, isStatus: false, showStatus: false }));

  const cells: ReturnType<typeof getApplicationRowCells> = [];
  columnIds.forEach(id => {
    const isStatus = ANALYSIS_RESULT_COLUMN_IDS.has(id);
    const previous = cells[cells.length - 1];
    if (isStatus && previous?.isStatus) {
      previous.colSpan += 1;
      return;
    }
    cells.push({ id, colSpan: 1, isStatus, showStatus: isStatus && !cells.some(cell => cell.isStatus) });
  });
  return cells;
};
